/**
 * Business detail page.
 * 
 * Displays comprehensive business information including:
 * - Business details and prompts
 * - Analysis reports
 * - Content checker results
 * - FAQ audits
 * - LLMs.txt generations
 * 
 * @module pages/BusinessDetailPage
 * @see CSDEV-141 - Business detail: render content checker / FAQ audit / LLMs.txt sections
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Globe,
  Calendar,
  User,
  FileText,
  MessageSquare,
  FileCheck,
  HelpCircle,
  FileCode,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';
import { Card, Badge, LoadingSection, ErrorState, EmptyState } from '../components/ui';
import {
  useBusiness,
  useBusinessReports,
  useBusinessContentChecker,
  useBusinessFAQAudits,
  useBusinessLLMsTxt,
  useAuditLog,
} from '../hooks';
import { AuditActionTypes, AuditResourceTypes } from '../lib/audit';
import { formatDate, formatDateTime } from '../lib/utils';

/**
 * Business detail page component.
 * 
 * @returns Business detail page element
 */
export function BusinessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { logAction } = useAuditLog();

  // Expanded state for collapsible sections
  const [expandedContent, setExpandedContent] = useState<string | null>(null);
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [expandedLLMs, setExpandedLLMs] = useState<string | null>(null);

  const { data: business, isLoading, error } = useBusiness(id);
  const { data: reports, isLoading: reportsLoading } = useBusinessReports(id);
  const { data: contentChecker, isLoading: contentLoading } = useBusinessContentChecker(id);
  const { data: faqAudits, isLoading: faqLoading } = useBusinessFAQAudits(id);
  const { data: llmsTxt, isLoading: llmsLoading } = useBusinessLLMsTxt(id);

  // Log page view
  useEffect(() => {
    if (business) {
      logAction(
        AuditActionTypes.VIEW_BUSINESS,
        AuditResourceTypes.BUSINESS,
        business.id,
        { business_name: business.business_name }
      );
    }
  }, [business, logAction]);

  if (isLoading) {
    return <LoadingSection text="Loading business details..." />;
  }

  if (error || !business) {
    return (
      <ErrorState
        message={error?.message || 'Business not found'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/businesses"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Businesses
      </Link>

      {/* Business header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{business.business_name}</h1>
          <p className="text-gray-500">{business.industry || 'No industry specified'}</p>
        </div>
        {business.website_url && (
          <a
            href={business.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary gap-2"
          >
            <Globe className="h-4 w-4" />
            Visit Website
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business info */}
        <Card title="Business Information" className="lg:col-span-2">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <User className="h-4 w-4" />
                Owner
              </dt>
              <dd className="mt-1">
                <Link
                  to={`/users/${business.profile_id}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {business.users?.email || business.profile_id}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Website
              </dt>
              <dd className="mt-1">
                {business.website_url ? (
                  <a
                    href={business.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary-600 hover:text-secondary-700"
                  >
                    {business.website_url}
                  </a>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Created
              </dt>
              <dd className="mt-1 text-gray-900">{formatDateTime(business.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-gray-900">{formatDateTime(business.updated_at)}</dd>
            </div>
          </dl>
        </Card>

        {/* Stats */}
        <Card title="Quick Stats">
          <dl className="space-y-4">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">Prompts</dt>
              <dd className="text-lg font-semibold">{business.prompts?.length || 0}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">Reports</dt>
              <dd className="text-lg font-semibold">{reports?.length || 0}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">Content Checks</dt>
              <dd className="text-lg font-semibold">{contentChecker?.length || 0}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">FAQ Audits</dt>
              <dd className="text-lg font-semibold">{faqAudits?.length || 0}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">LLMs.txt</dt>
              <dd className="text-lg font-semibold">{llmsTxt?.length || 0}</dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* Configured prompts */}
      {business.prompts && business.prompts.length > 0 && (
        <Card title={`Configured Prompts (${business.prompts.length})`}>
          <div className="space-y-3">
            {business.prompts.map((prompt, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <MessageSquare className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{prompt}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Analysis reports */}
      <Card
        title={`Analysis Reports (${reports?.length || 0})`}
        action={
          reports && reports.length > 0 && (
            <Link
              to={`/reports?business=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          )
        }
      >
        {reportsLoading ? (
          <LoadingSection text="Loading reports..." />
        ) : reports && reports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>AI Score</th>
                  <th>Mentions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.slice(0, 5).map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="text-gray-500">{formatDate(report.created_at)}</td>
                    <td>
                      <Badge
                        variant={
                          report.status === 'completed'
                            ? 'success'
                            : report.status === 'failed'
                            ? 'error'
                            : report.status === 'running'
                            ? 'warning'
                            : 'neutral'
                        }
                        size="sm"
                      >
                        {report.status}
                      </Badge>
                    </td>
                    <td>
                      {report.avg_ai_visibility_score !== null
                        ? `${(report.avg_ai_visibility_score * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td>{report.total_mentions ?? '—'}</td>
                    <td>
                      <Link
                        to={`/reports/${report.id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No reports"
            description="No analysis reports have been generated for this business."
            icon={<FileText className="h-8 w-8 text-gray-400" />}
          />
        )}
      </Card>

      {/* Content Checker Results */}
      <Card
        title={`Content Checker (${contentChecker?.length || 0})`}
        action={
          contentChecker && contentChecker.length > 0 && (
            <Link
              to={`/content?business=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          )
        }
      >
        {contentLoading ? (
          <LoadingSection text="Loading content checks..." />
        ) : contentChecker && contentChecker.length > 0 ? (
          <div className="space-y-4">
            {contentChecker.slice(0, 5).map((check) => (
              <div
                key={check.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedContent(expandedContent === check.id ? null : check.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileCheck className="h-5 w-5 text-primary-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900 truncate max-w-md">
                        {check.url}
                      </p>
                      <p className="text-sm text-gray-500">{formatDate(check.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Answer Readiness</p>
                      <p className="font-semibold text-gray-900">
                        {check.answer_readiness_score !== null
                          ? `${check.answer_readiness_score.toFixed(0)}%`
                          : '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Conversational</p>
                      <p className="font-semibold text-gray-900">
                        {check.conversational_tone_score !== null
                          ? `${check.conversational_tone_score.toFixed(0)}%`
                          : '—'}
                      </p>
                    </div>
                    {expandedContent === check.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedContent === check.id && check.recommendations && check.recommendations.length > 0 && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendations</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {check.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm text-gray-600">{rec}</li>
                      ))}
                    </ul>
                    <a
                      href={check.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View URL
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No content checks"
            description="No content checker audits have been run for this business."
            icon={<FileCheck className="h-8 w-8 text-gray-400" />}
          />
        )}
      </Card>

      {/* FAQ Audits */}
      <Card
        title={`FAQ Audits (${faqAudits?.length || 0})`}
        action={
          faqAudits && faqAudits.length > 0 && (
            <Link
              to={`/faq-audits?business=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          )
        }
      >
        {faqLoading ? (
          <LoadingSection text="Loading FAQ audits..." />
        ) : faqAudits && faqAudits.length > 0 ? (
          <div className="space-y-4">
            {faqAudits.slice(0, 5).map((audit) => (
              <div
                key={audit.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === audit.id ? null : audit.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="h-5 w-5 text-secondary-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">
                        FAQ Audit - {formatDate(audit.created_at)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {audit.recommended_faq?.length || 0} recommended FAQs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">FAQ Score</p>
                      <p className="font-semibold text-gray-900">
                        {audit.faq_score !== null ? `${audit.faq_score.toFixed(0)}%` : '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Schema Score</p>
                      <p className="font-semibold text-gray-900">
                        {audit.schema_score !== null ? `${audit.schema_score.toFixed(0)}%` : '—'}
                      </p>
                    </div>
                    {expandedFAQ === audit.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedFAQ === audit.id && audit.recommended_faq && audit.recommended_faq.length > 0 && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Recommended FAQs</h4>
                    <div className="space-y-3">
                      {audit.recommended_faq.slice(0, 5).map((faq, idx) => {
                        const faqRecord = faq as Record<string, unknown>;
                        const question =
                          typeof faqRecord.question === 'string'
                            ? faqRecord.question
                            : `Question ${idx + 1}`;
                        const answer =
                          typeof faqRecord.answer === 'string' ? faqRecord.answer : null;

                        return (
                          <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                            <p className="text-sm font-medium text-gray-900">Q: {question}</p>
                            {answer && (
                              <p className="text-sm text-gray-600 mt-1">A: {answer}</p>
                            )}
                          </div>
                        );
                      })}
                      {audit.recommended_faq.length > 5 && (
                        <p className="text-sm text-gray-500">
                          +{audit.recommended_faq.length - 5} more FAQs
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No FAQ audits"
            description="No FAQ audits have been run for this business."
            icon={<HelpCircle className="h-8 w-8 text-gray-400" />}
          />
        )}
      </Card>

      {/* LLMs.txt Generations */}
      <Card
        title={`LLMs.txt (${llmsTxt?.length || 0})`}
        action={
          llmsTxt && llmsTxt.length > 0 && (
            <Link
              to={`/llms-txt?business=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          )
        }
      >
        {llmsLoading ? (
          <LoadingSection text="Loading LLMs.txt generations..." />
        ) : llmsTxt && llmsTxt.length > 0 ? (
          <div className="space-y-4">
            {llmsTxt.slice(0, 5).map((gen) => (
              <div
                key={gen.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedLLMs(expandedLLMs === gen.id ? null : gen.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileCode className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">
                        LLMs.txt - {formatDate(gen.created_at)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {gen.recommendations?.length || 0} recommendations
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={gen.llms_txt_content ? 'success' : 'neutral'}
                      size="sm"
                    >
                      {gen.llms_txt_content ? 'Generated' : 'Pending'}
                    </Badge>
                    {expandedLLMs === gen.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedLLMs === gen.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
                    {gen.llms_txt_content && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">LLMs.txt Content</h4>
                        <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto max-h-48">
                          {gen.llms_txt_content}
                        </pre>
                      </div>
                    )}
                    {gen.recommendations && gen.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendations</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {gen.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-gray-600">{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No LLMs.txt generations"
            description="No LLMs.txt files have been generated for this business."
            icon={<FileCode className="h-8 w-8 text-gray-400" />}
          />
        )}
      </Card>
    </div>
  );
}
